/// <reference path="./global.d.ts" />
import { useEffect, useRef } from 'react';
import { getChatbotSdkUrl } from './config';
import type { ChatbotEnv, ChatbotGrayGateProps } from './types';
import { useChatbotCore } from './useChatbotCore';

function isSubAccount(): boolean {
  if (typeof document === 'undefined') return false;
  const match = document.cookie.match(/(?:^|; )Global-AccountType=([^;]*)/);
  return match?.[1]?.trim() === 'NORMAL_SUB_ACCOUNT';
}

const ZENDESK_ID = '2a42d5dd-9844-4f34-b2a8-6aa090afb7e9';

function injectZendeskWidget(locale: string) {
  if (document.getElementById('ze-snippet')) return;
  const script = document.createElement('script');
  script.id = 'ze-snippet';
  script.async = true;
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_ID}`;
  script.onload = () => {
    try {
      if ('zE' in window) {
        const localeMap: Record<string, string> = { en: 'en-US', 'zh-tw': 'zh-tw', 'uk-ua': 'en' };
        (window as any).zE('messenger:set', 'locale', localeMap[locale] || 'en-US');
      }
    } catch (_) {}
  };
  document.body.appendChild(script);
}

function ChatbotInit({
  theme,
  env,
  pageType,
  locale,
}: {
  theme: 'dark' | 'light';
  env: ChatbotEnv;
  pageType?: 'trade' | 'default';
  locale: string;
}) {
  const token = env.getToken() || '';
  useChatbotCore({ theme, env, token, pageType, locale });
  return null;
}

function injectChatbotSdk(sdkUrl: string) {
  if (document.getElementById('chatbot-sdk')) return;

  const polyfill = document.createElement('script');
  polyfill.id = 'chatbot-process-polyfill';
  polyfill.textContent =
    'if(typeof window!=="undefined"&&!window.process){window.process={env:{NODE_ENV:"production"}}}';
  document.body.appendChild(polyfill);

  const t = Math.floor(Date.now() / 300000);
  const script = document.createElement('script');
  script.id = 'chatbot-sdk';
  script.async = true;
  script.src = `${sdkUrl}?t=${t}`;
  document.body.appendChild(script);
}

export interface ChatbotGrayGateCoreProps extends ChatbotGrayGateProps {
  /** 当前 locale，由外层（Pages 或 App Router）注入 */
  locale: string;
}

/**
 * 路由库无关的 ChatbotGrayGate 核心实现。
 *
 * 灰度已全量开放、Zendesk 兜底已废弃：所有用户统一注入 chatbot SDK。
 * KR 站不接入（isKrSite=true 时直接返回）。
 *
 * Pages Router 用 ./ChatbotGrayGate 包装，App Router 用 ./ChatbotGrayGate.app 包装；
 * 二者只负责把 locale 注入。
 */
export function ChatbotGrayGateCore({ theme, env, isKrSite = false, pageType, locale }: ChatbotGrayGateCoreProps) {
  // 一次会话内只注入一次 SDK
  const decidedRef = useRef(false);

  useEffect(() => {
    if (isKrSite) return;
    if (decidedRef.current) return;
    decidedRef.current = true;

    if (isSubAccount()) {
      injectZendeskWidget(locale);
    } else {
      injectChatbotSdk(getChatbotSdkUrl(env.navUrl, env.cdnFallback));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isKrSite || isSubAccount()) return null;

  return <ChatbotInit theme={theme} env={env} pageType={pageType} locale={locale} />;
}
